import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, inject } from '@angular/core';
import { catchError, forkJoin, map, of } from 'rxjs';
import { ListFilter, Tenant } from '@parashift/shared/models';
import { SsoIdentityProviderService } from '@parashift/shared/services';

interface PolicyMethod {
  title: string;
  note: string;
}

@Component({
  selector: 'pdc-authentication-policy',
  templateUrl: './pdc-authentication-policy.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PdcAuthenticationPolicyComponent implements OnInit {
  private ssoService = inject(SsoIdentityProviderService);
  private cdr = inject(ChangeDetectorRef);

  @Input({ required: true }) tenant: Tenant;

  // One entry per allowed sign-in method (OR); title = factors (AND), note = plain-language description.
  methods: PolicyMethod[] = [];

  private readonly factorLabels: Record<string, string> = {
    psw: $localize `:@@components.pdc_authentication_policy.factor.psw:Password`,
    otp: $localize `:@@components.pdc_authentication_policy.factor.otp:2FA (TOTP)`,
    microsoft_graph: $localize `:@@components.pdc_authentication_policy.factor.microsoft:Microsoft`
  };
  private readonly ssoLoginLabel = $localize `:@@components.pdc_authentication_policy.factor.sso:Log in with SSO`;

  private readonly notes: Record<string, string> = {
    psw: $localize `:@@components.pdc_authentication_policy.note.psw:Users log in with their email and a password (min. 12 characters, incl. uppercase, lowercase, a number and a special character).`,
    'psw+otp': $localize `:@@components.pdc_authentication_policy.note.psw_otp:Users log in with their email, a password and an authenticator app.`,
    microsoft_graph: $localize `:@@components.pdc_authentication_policy.note.microsoft:Users use the "Sign in with Microsoft" button.`
  };
  private readonly ssoNote = $localize `:@@components.pdc_authentication_policy.note.sso:Users use the "Log in with SSO" button.`;

  get isDenyAll(): boolean {
    return (this.tenant?.factors_required ?? []).length === 0;
  }

  ngOnInit() {
    this.buildMethods();
  }

  private buildMethods() {
    const requirements = this.tenant?.factors_required ?? [];
    const oidcIds = [...new Set(
      requirements
        .flatMap(requirement => requirement.split('+'))
        .map(factor => factor.match(/^oidc:(\d+)$/)?.[1])
        .filter((id): id is string => !!id)
    )];

    if (oidcIds.length === 0) {
      this.methods = requirements.map(requirement => this.toMethod(requirement, {}));
      return;
    }

    forkJoin(oidcIds.map(id =>
      this.ssoService.findAll(new ListFilter({ filter: { id } })).pipe(
        map(response => ({ id, name: response.getModels()[0]?.name })),
        catchError(() => of({ id, name: undefined }))
      )
    )).subscribe(results => {
      const nameById: Record<string, string> = {};
      results.forEach(({ id, name }) => { if (name) { nameById[id] = name; } });
      this.methods = requirements.map(requirement => this.toMethod(requirement, nameById));
      this.cdr.markForCheck();
    });
  }

  private toMethod(requirement: string, nameById: Record<string, string>): PolicyMethod {
    const title = requirement
      .split('+')
      .map(token => this.factorTitle(token, nameById))
      .join(' + ');

    const oidc = requirement.match(/^oidc:(\d+)$/);
    const note = oidc ? this.ssoNote : (this.notes[requirement] ?? '');

    return { title, note };
  }

  private factorTitle(token: string, nameById: Record<string, string>): string {
    const oidc = token.match(/^oidc:(\d+)$/);
    if (oidc) {
      const name = nameById[oidc[1]];
      return name ? `${this.ssoLoginLabel} - ${name}` : this.ssoLoginLabel;
    }
    return this.factorLabels[token] ?? token;
  }
}
